import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Play, Clock, CheckCircle, XCircle, Loader2, Plus } from 'lucide-react';
import { clsx } from 'clsx';
import { useJobs } from '../hooks/useVideo';
import type { VideoJob } from '../types';

function JobCard({ job }: { job: VideoJob }) {
  const statusConfig = {
    pending: { icon: Clock, color: 'text-yellow-400', bg: 'bg-yellow-400/10', animate: false },
    processing: { icon: Loader2, color: 'text-blue-400', bg: 'bg-blue-400/10', animate: true },
    completed: { icon: CheckCircle, color: 'text-green-400', bg: 'bg-green-400/10', animate: false },
    failed: { icon: XCircle, color: 'text-red-400', bg: 'bg-red-400/10', animate: false },
    cancelled: { icon: XCircle, color: 'text-gray-400', bg: 'bg-gray-400/10', animate: false },
  };

  const config = statusConfig[job.status];
  const Icon = config.icon;

  return (
    <Link to={`/jobs/${job.id}`}>
      <motion.div
        whileHover={{ scale: 1.02 }}
        className="card hover:border-primary-500/50 transition-all cursor-pointer"
      >
        {/* Thumbnail / Preview */}
        <div className="aspect-video bg-gray-800 rounded-lg mb-4 overflow-hidden relative">
          {job.status === 'completed' && job.videoUrl ? (
            <video
              src={job.videoUrl}
              className="w-full h-full object-cover"
              muted
              loop
              onMouseEnter={(e) => e.currentTarget.play()}
              onMouseLeave={(e) => {
                e.currentTarget.pause();
                e.currentTarget.currentTime = 0;
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <div className={clsx('p-4 rounded-full', config.bg)}>
                <Icon className={clsx('w-8 h-8', config.color, config.animate && 'animate-spin')} />
              </div>
            </div>
          )}

          {/* Progress overlay */}
          {job.status === 'processing' && (
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-700">
              <div
                className="h-full bg-primary-500 transition-all"
                style={{ width: `${job.progress}%` }}
              />
            </div>
          )}

          {/* Play button for completed videos */}
          {job.status === 'completed' && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 hover:opacity-100 transition-opacity">
              <Play className="w-12 h-12 text-white" />
            </div>
          )}
        </div>

        {/* Info */}
        <div className="space-y-2">
          <p className="text-sm text-gray-300 line-clamp-2">{job.prompt.text}</p>
          <div className="flex items-center justify-between text-xs">
            <span className={clsx('px-2 py-1 rounded-full', config.bg, config.color)}>
              {job.status}
            </span>
            <span className="text-gray-500">
              {new Date(job.createdAt).toLocaleDateString()}
            </span>
          </div>
        </div>
      </motion.div>
    </Link>
  );
}

export default function GalleryPage() {
  const { data, isLoading } = useJobs({ limit: 20 });

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-12"
        >
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">My Videos</h1>
            <p className="text-gray-400">
              {data?.pagination.total || 0} videos generated
            </p>
          </div>
          <Link to="/generate" className="btn-primary flex items-center space-x-2">
            <Plus className="w-5 h-5" />
            <span>New Video</span>
          </Link>
        </motion.div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
          </div>
        ) : data?.jobs.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20"
          >
            <div className="w-16 h-16 rounded-full bg-gray-800 flex items-center justify-center mx-auto mb-4">
              <Play className="w-8 h-8 text-gray-500" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">No videos yet</h3>
            <p className="text-gray-400 mb-6">Create your first AI-generated video</p>
            <Link to="/generate" className="btn-primary">
              Generate Video
            </Link>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
          >
            {data?.jobs.map((job, index) => (
              <motion.div
                key={job.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <JobCard job={job} />
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
}
